import {
  IPartProTradeCategory,
  IUpdatePartProTradeCategory,
} from "../models/partPro/IPartProTradeSettings";

/**
 * Local selection state for the PartPro trade category picker. Kept as pure functions so
 * the rules - default implies offered, clearing drops the default, select-all only touches
 * what is shown - are pinned by tests rather than by clicking through ~880 rows.
 */
export type TradeCategorySelection = {
  offered: Set<string>;
  defaults: Set<string>;
};

export function selectionFromSettings(categories: IPartProTradeCategory[]): TradeCategorySelection {
  return {
    offered: new Set(categories.filter((c) => c.offered).map((c) => c.id)),
    defaults: new Set(categories.filter((c) => c.offered && c.defaultForNewBuyers).map((c) => c.id)),
  };
}

export function filterCategories(categories: IPartProTradeCategory[], query: string): IPartProTradeCategory[] {
  const q = query.trim().toLowerCase();
  if (!q) return categories;
  return categories.filter((c) => c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q));
}

export function toggleOffered(selection: TradeCategorySelection, id: string, on: boolean): TradeCategorySelection {
  const offered = new Set(selection.offered);
  const defaults = new Set(selection.defaults);
  if (on) {
    offered.add(id);
  } else {
    offered.delete(id);
    defaults.delete(id);
  }
  return { offered, defaults };
}

/** Marking a category default for new buyers also offers it - a default that is not offered is meaningless. */
export function toggleDefault(selection: TradeCategorySelection, id: string, on: boolean): TradeCategorySelection {
  const offered = new Set(selection.offered);
  const defaults = new Set(selection.defaults);
  if (on) {
    offered.add(id);
    defaults.add(id);
  } else {
    defaults.delete(id);
  }
  return { offered, defaults };
}

export function selectAllShown(selection: TradeCategorySelection, shown: IPartProTradeCategory[]): TradeCategorySelection {
  const offered = new Set(selection.offered);
  shown.forEach((c) => offered.add(c.id));
  return { offered, defaults: new Set(selection.defaults) };
}

export function clearShown(selection: TradeCategorySelection, shown: IPartProTradeCategory[]): TradeCategorySelection {
  const offered = new Set(selection.offered);
  const defaults = new Set(selection.defaults);
  shown.forEach((c) => {
    offered.delete(c.id);
    defaults.delete(c.id);
  });
  return { offered, defaults };
}

/**
 * The PUT body: every offered category keyed by its Wilma id where it has one, otherwise
 * by slug (PartPro custom categories). Order follows the category list so the payload is
 * stable between saves.
 */
export function buildCategoryUpdate(
  categories: IPartProTradeCategory[],
  selection: TradeCategorySelection
): IUpdatePartProTradeCategory[] {
  return categories
    .filter((c) => selection.offered.has(c.id))
    .map((c) =>
      c.externalId
        ? { externalId: c.externalId, defaultForNewBuyers: selection.defaults.has(c.id) }
        : { slug: c.slug, defaultForNewBuyers: selection.defaults.has(c.id) }
    );
}

export function selectionEquals(a: TradeCategorySelection, b: TradeCategorySelection): boolean {
  if (a.offered.size !== b.offered.size || a.defaults.size !== b.defaults.size) return false;
  for (const id of Array.from(a.offered)) if (!b.offered.has(id)) return false;
  for (const id of Array.from(a.defaults)) if (!b.defaults.has(id)) return false;
  return true;
}
